import { useCallback, useEffect, useState } from 'react';
import { WorkoutPlanEditor } from './WorkoutPlanEditor';
import { PremiumCard, PrimaryButton } from './premium';
import { api, ApiError } from '../lib/api';
import type { SavedPlanListRow, SavedProgramData, WorkoutPlanResult } from '../coachTypes';

function emptyPlan(): WorkoutPlanResult {
  return {
    summary: 'My program',
    split: 'Custom',
    days: [
      {
        day: 'Monday',
        focus: 'Day 1',
        exercises: [],
      },
    ],
    progressionNotes: [],
  };
}

type EditorMode = 'closed' | 'edit' | 'create';

function ProgramOption({
  title,
  subtitle,
  selected,
  onClick,
}: {
  title: string;
  subtitle?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-2xl border px-3.5 py-3 text-left transition-all duration-150 active:scale-[0.99] ${
        selected
          ? 'border-sky-400/40 bg-sky-500/12 shadow-[0_0_20px_rgba(56,189,248,0.12)]'
          : 'border-white/10 bg-white/[0.04] hover:border-white/16 hover:bg-white/[0.06]'
      }`}
    >
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
          selected ? 'border-sky-400 bg-sky-500/25' : 'border-white/20 bg-black/20'
        }`}
        aria-hidden
      >
        {selected ? <span className="h-2 w-2 rounded-full bg-sky-300" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm font-semibold ${selected ? 'text-sky-100' : 'text-[var(--text-primary)]'}`}>
          {title}
        </span>
        {subtitle ? <span className="mt-0.5 block truncate text-[11px] text-[var(--text-muted)]">{subtitle}</span> : null}
      </span>
    </button>
  );
}

export function ActiveProgramManager({
  savedPlanRows,
  activePlanId,
  onActivePlanChange,
  onSaved,
}: {
  savedPlanRows: SavedPlanListRow[];
  activePlanId: string | null | undefined;
  onActivePlanChange: (planId: string) => Promise<void>;
  onSaved: () => void;
}) {
  const [editorMode, setEditorMode] = useState<EditorMode>('closed');
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [planTitle, setPlanTitle] = useState('My program');
  const [planDraft, setPlanDraft] = useState<WorkoutPlanResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const closeEditor = useCallback(() => {
    setEditorMode('closed');
    setEditingPlanId(null);
    setPlanDraft(null);
    setMsg(null);
  }, []);

  async function openEdit() {
    const id = activePlanId?.trim();
    if (!id) {
      setMsg('Select a program first, or tap Create.');
      return;
    }
    setMsg(null);
    setBusy(true);
    try {
      const row = await api<{ id: string; title: string; data: SavedProgramData }>(
        `/api/saved-workout-plans/${id}`,
      );
      setEditingPlanId(row.id);
      setPlanTitle(row.title);
      setPlanDraft(structuredClone(row.data?.plan ?? emptyPlan()));
      setEditorMode('edit');
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not load program.');
    } finally {
      setBusy(false);
    }
  }

  function openCreate() {
    setEditingPlanId(null);
    setPlanTitle('My program');
    setPlanDraft(emptyPlan());
    setEditorMode('create');
    setMsg(null);
  }

  async function savePlan() {
    if (!planDraft) return;
    setBusy(true);
    setMsg(null);
    try {
      const data: SavedProgramData = { plan: planDraft, split: null, form: {} };
      const title = planTitle.trim() || 'My program';
      if (editorMode === 'edit' && editingPlanId) {
        await api(`/api/saved-workout-plans/${editingPlanId}`, {
          method: 'PUT',
          body: JSON.stringify({ title, data }),
        });
        setMsg('Program updated.');
      } else {
        const created = await api<{ id: string }>('/api/saved-workout-plans', {
          method: 'POST',
          body: JSON.stringify({ title, data }),
        });
        await onActivePlanChange(created.id);
        setEditingPlanId(created.id);
        setEditorMode('edit');
        setMsg('Program created and set as active.');
      }
      onSaved();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not save program.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (editorMode === 'closed') return;
    if (editorMode === 'edit' && activePlanId && activePlanId !== editingPlanId) {
      closeEditor();
    }
  }, [activePlanId, closeEditor, editorMode, editingPlanId]);

  return (
    <div className="space-y-3">
      <PremiumCard className="!p-4">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--text-muted)]">Active program</p>
        <div className="space-y-2">
          <ProgramOption
            title="None"
            subtitle="Train without a structured program"
            selected={!activePlanId}
            onClick={() => void onActivePlanChange('')}
          />
          {savedPlanRows.map((row) => (
            <ProgramOption
              key={row.id}
              title={row.title}
              subtitle="Saved training block"
              selected={activePlanId === row.id}
              onClick={() => void onActivePlanChange(row.id)}
            />
          ))}
        </div>

        {savedPlanRows.length === 0 ? (
          <p className="mt-3 text-xs text-[var(--text-muted)]">No saved programs yet. Create one to guide your training week.</p>
        ) : null}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <PrimaryButton variant="secondary" className="!min-h-[44px] !text-sm" disabled={busy || !activePlanId} onClick={() => void openEdit()}>
            Edit
          </PrimaryButton>
          <PrimaryButton className="!min-h-[44px] !text-sm" disabled={busy} onClick={openCreate}>
            Create
          </PrimaryButton>
        </div>

        {msg && editorMode === 'closed' ? <p className="mt-3 text-xs text-[var(--text-muted)]">{msg}</p> : null}
      </PremiumCard>

      {editorMode !== 'closed' && planDraft ? (
        <PremiumCard variant="train" className="!p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-display text-base font-bold text-[var(--text-primary)]">
              {editorMode === 'create' ? 'New program' : 'Edit program'}
            </h3>
            <button
              type="button"
              onClick={closeEditor}
              className="rounded-xl px-2.5 py-1.5 text-xs font-semibold text-[var(--text-muted)] hover:bg-white/[0.06] hover:text-[var(--text-secondary)]"
            >
              Close
            </button>
          </div>

          <label className="mt-4 block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Title</span>
            <input
              value={planTitle}
              onChange={(e) => setPlanTitle(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-base text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)] focus:border-sky-500/35 focus:shadow-[0_0_0_3px_rgba(56,189,248,0.1)] sm:text-sm"
            />
          </label>

          <div className="mt-4">
            <WorkoutPlanEditor plan={planDraft} onChange={setPlanDraft} />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <PrimaryButton className="!min-h-[48px]" disabled={busy} onClick={() => void savePlan()}>
              {busy ? 'Saving…' : editorMode === 'create' ? 'Save program' : 'Save changes'}
            </PrimaryButton>
            {msg ? <span className="text-xs text-[var(--text-muted)]">{msg}</span> : null}
          </div>
        </PremiumCard>
      ) : null}
    </div>
  );
}
